import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStopwatch, faHeartPulse, faPencil, faTriangleExclamation,
  faBolt, faTrophy, faMedal, faRobot, faStar, faHandcuffs,
  faCoins, faSearch, faBed, faCrown, faLayerGroup, faFire, faClock,
} from '@fortawesome/free-solid-svg-icons';
import { Activity, DailyChallenge } from '../types/activity';
import { useScrollDismiss } from '../hooks/useScrollDismiss';
import { getSportIcon } from '../utils/sportEmoji';
import { formatDuration } from '../utils/dateUtils';
import { formatPoints } from '../utils/scoring';

interface Props {
  activity: Activity;
  variant: 'mine' | 'opponent';
  ownerAvatar?: string | null;
  isBot?: boolean;
  showOppPoints?: boolean;
  hideTooltip?: boolean;
  challenge?: DailyChallenge | null;
}

function buildTooltip(activity: Activity, challenge?: DailyChallenge | null): React.ReactNode {
  const base = Number(activity.base_points) || 0;
  const challengePts = Number(activity.challenge_bonus_points) || 0;
  const total = Number(activity.total_points) || 0;
  const prCount = activity.pr_count ?? 0;
  const achRaw = activity.achievement_count ?? 0;
  const achCount = Math.min(achRaw, 25);
  const isManual = !!activity.is_manual_entry;
  const isLate = !!activity.late_synced;
  const hasHR = !!activity.avg_heart_rate;
  const durationMin = Math.round(activity.duration_seconds / 60);
  const isConsolation = activity.sport_type === 'Consolation';
  const isRestDay = activity.sport_type === 'RestDay';
  const isBetting = activity.sport_type === 'Betting';
  const isChallengeKing = activity.sport_type === 'ChallengeKing';
  const isMultisport = activity.sport_type === 'Multisport';
  const isSportJunky = activity.sport_type === 'SportJunky';
  const isDailySpin = activity.sport_type === 'DailySpin';
  const isSpecial = isConsolation || isRestDay || isBetting || isChallengeKing || isMultisport || isSportJunky || isDailySpin || activity.sport_type === 'BadgeReward';

  const name = activity.activity_name
    ? `${activity.sport_type}: ${activity.activity_name}`
    : activity.sport_type;

  const rows: React.ReactNode[] = [
    <div key="title" className="tip-title">{name}</div>,
    ...(!isSpecial ? [
      <div key="dur" className="tip-row tip-meta"><FontAwesomeIcon icon={faStopwatch} className="icon-muted" /> {formatDuration(activity.duration_seconds)}{activity.avg_heart_rate ? <> · <FontAwesomeIcon icon={faHeartPulse} className="icon-red" /> {Math.round(activity.avg_heart_rate)} bpm</> : ''}</div>,
      <div key="sep" className="tip-sep" />,
    ] : []),
  ];

  if (isConsolation) {
    // Consolation points from previous week's duel – no duration, just points
    rows.push(<div key="consolation" className="tip-row tip-bonus">Consolation from last week (20% of lost duel points)</div>);
    rows.push(<div key="total" className="tip-row tip-total">Total: <strong>{formatPoints(total)}</strong></div>);
    return rows;
  } else if (isRestDay) {
    // Rest Day from item – no duration, just points
    rows.push(<div key="restday" className="tip-row tip-bonus"><FontAwesomeIcon icon={faBed} /> Rest Day activated via item</div>);
    rows.push(<div key="total" className="tip-row tip-total">Total: <strong>{formatPoints(total)}</strong></div>);
    return rows;
  } else if (activity.sport_type === 'Betting') {
    // Betting result – no duration, just net points
    const netPts = total;
    const label = netPts >= 0 ? `Won ${formatPoints(netPts)}` : `Lost ${formatPoints(Math.abs(netPts))}`;
    rows.push(<div key="betting" className="tip-row tip-bonus"><FontAwesomeIcon icon={faCoins} /> 💰 Betting result: {label}</div>);
    rows.push(<div key="total" className="tip-row tip-total">Total: <strong>{formatPoints(total)}</strong></div>);
    return rows;
  } else if (isChallengeKing) {
    rows.push(<div key="challengeking" className="tip-row tip-bonus"><FontAwesomeIcon icon={faCrown} /> Challenge King bonus: <strong>+{formatPoints(total)}</strong></div>);
    rows.push(<div key="total" className="tip-row tip-total">Total: <strong>{formatPoints(total)}</strong></div>);
    return rows;
  } else if (isMultisport) {
    rows.push(<div key="multisport" className="tip-row tip-bonus"><FontAwesomeIcon icon={faLayerGroup} /> Multisport bonus: <strong>+{formatPoints(total)}</strong></div>);
    rows.push(<div key="total" className="tip-row tip-total">Total: <strong>{formatPoints(total)}</strong></div>);
    return rows;
  } else if (isSportJunky) {
    rows.push(<div key="sportjunky" className="tip-row tip-bonus"><FontAwesomeIcon icon={faFire} /> Sport Junky bonus: <strong>+{formatPoints(total)}</strong></div>);
    rows.push(<div key="total" className="tip-row tip-total">Total: <strong>{formatPoints(total)}</strong></div>);
    return rows;
  } else if (activity.sport_type === 'BadgeReward') {
    let raw: any;
    try { raw = typeof (activity as any).raw_json === 'string' ? JSON.parse((activity as any).raw_json) : (activity as any).raw_json; } catch { raw = null; }
    const badges = raw?.badges;
    if (Array.isArray(badges)) {
      rows.push(<div key="badge-title" className="tip-row tip-bonus"><FontAwesomeIcon icon={faMedal} /> Weekly Badge Rewards:</div>);
      for (const b of badges) {
        rows.push(<div key={b.code} className="tip-row tip-zone"><FontAwesomeIcon icon={getSportIcon(b.icon || 'BadgeReward')} /> {b.name} (Tier {b.tier}): <strong>+{b.weekly_reward} pts/week</strong></div>);
      }
    }
    rows.push(<div key="total" className="tip-row tip-total">Total: <strong>{formatPoints(total)}</strong></div>);
    return rows;
  } else if (isLate) {
    rows.push(<div key="late" className="tip-row tip-zero"><FontAwesomeIcon icon={faClock} /> {activity.late_reason === 'after_deadline' ? 'Started after the duel deadline' : 'Uploaded after the duel evaluation'} → <strong>0 points</strong></div>);
  } else if (isManual) {
    rows.push(<div key="manual" className="tip-row tip-zero"><FontAwesomeIcon icon={faPencil} /> Manually entered → <strong>0 points</strong></div>);
  } else if (activity.duration_seconds < 300) {
    rows.push(<div key="tooshort" className="tip-row tip-zero"><FontAwesomeIcon icon={faTriangleExclamation} /> Too short (&lt; 5 min) → <strong>0 points</strong> and no challenge bonus</div>);
  } else {
    if (!hasHR) {
      rows.push(
        <div key="hr" className="tip-row tip-warn"><FontAwesomeIcon icon={faTriangleExclamation} /> No HR monitor → reduced points (0.20 pts/min)</div>,
        <div key="base" className="tip-row">Base: 0.20 pts/min × {durationMin} min = <strong>{formatPoints(base)}</strong></div>
      );
    } else if (activity.zone_breakdown && activity.zone_breakdown.length > 0) {
      const ZONE_NAMES = ['Rest', 'Recovery', 'Endurance', 'Tempo', 'Threshold', 'Anaerobic'];
      const ZONE_COLORS = ['#9e9e9e', '#4caf77', '#2196f3', '#ffb63c', '#ff9800', '#f44336'];
      rows.push(<div key="base" className="tip-row"><FontAwesomeIcon icon={faHeartPulse} className="icon-red" /> HR zone breakdown:</div>);
      for (const zb of activity.zone_breakdown) {
        const zMin = Math.round(zb.time_sec / 60);
        if (zMin > 0) {
          rows.push(
            <div key={`z${zb.zone}`} className="tip-row tip-zone">
              <span className="tip-zone-dot" style={{ background: ZONE_COLORS[zb.zone] }} />
              <span className="tip-zone-name">Zone {zb.zone} ({ZONE_NAMES[zb.zone]})</span>
              <span className="tip-zone-time">{zMin} min</span>
              <span className="tip-zone-pts">{formatPoints(zb.points)} pts</span>
            </div>
          );
        }
      }
      rows.push(<div key="base-total" className="tip-row"><FontAwesomeIcon icon={faHeartPulse} className="icon-red" /> Base total: <strong>{formatPoints(base)}</strong></div>);
    } else {
      rows.push(<div key="base" className="tip-row"><FontAwesomeIcon icon={faHeartPulse} className="icon-red" /> HR zone calculation = <strong>{formatPoints(base)}</strong></div>);
    }
    if (challengePts > 0) {
      const isTriathlon = challenge?.type === 'triathlon';
      const hitsCap  = challengePts >= 150 && !isTriathlon;
      const capNote  = hitsCap ? ' (capped at 150 pts)' : '';
      const chalTitle = challenge?.title ?? 'Daily Challenge';
      const trophyAmt = activity.trophy_boosted ? (Number(activity.trophy_boost_amount) || 0) : 0;
      const displayPts = Math.max(0, challengePts - trophyAmt);
      if (displayPts > 0) {
        rows.push(<div key="chal" className="tip-row tip-bonus"><FontAwesomeIcon icon={faMedal} /> {chalTitle}{capNote}: <strong>+{formatPoints(displayPts)}</strong></div>);
      }
    } else if (challenge && activity.challenge_miss_reason) {
      // Show why this activity didn't qualify for the daily challenge
      rows.push(<div key="miss-reason" className="tip-row tip-warn"><FontAwesomeIcon icon={faTriangleExclamation} /> {activity.challenge_miss_reason} → <strong>No challenge bonus</strong></div>);
    }
    // Show flagging warnings (speed implausible, unrealistic HR)
    if (activity.flag_reason === 'speed_implausible') {
      rows.push(<div key="speed-flag" className="tip-row tip-warn"><FontAwesomeIcon icon={faTriangleExclamation} /> Implausible speed for {activity.sport_type} → flagged for review</div>);
    } else if (activity.hr_flagged) {
      rows.push(<div key="hr-flag" className="tip-row tip-warn"><FontAwesomeIcon icon={faTriangleExclamation} /> Unrealistic HR data → flagged for review</div>);
    }
    if (prCount > 0) {
      rows.push(<div key="pr" className="tip-row tip-pr"><FontAwesomeIcon icon={faTrophy} /> {prCount} Personal Best{prCount > 1 ? 's' : ''} × 1 pt = <strong>+{prCount}P</strong></div>);
    }
    if (achCount > 0) {
      rows.push(<div key="ach" className="tip-row tip-pr"><FontAwesomeIcon icon={faBolt} /> {achRaw} Achievement{achRaw > 1 ? 's' : ''} × 1 pt (max 25) = <strong>+{achCount}P</strong></div>);
    }
    if (activity.trophy_boosted && activity.trophy_boost_amount) {
      rows.push(<div key="trophy" className="tip-row tip-bonus"><FontAwesomeIcon icon={faTrophy} /> Trophy points: <strong>+{formatPoints(activity.trophy_boost_amount)}</strong></div>);
    }
    const parts = [formatPoints(base)];
    if (challengePts > 0) {
      const trophyAmt = activity.trophy_boosted ? (Number(activity.trophy_boost_amount) || 0) : 0;
      const displayPts = Math.max(0, challengePts - trophyAmt);
      if (displayPts > 0) parts.push(`+${formatPoints(displayPts)}`);
    }
    if (activity.trophy_boosted && activity.trophy_boost_amount) parts.push(`+${formatPoints(activity.trophy_boost_amount)}T`);
    if (prCount > 0)   parts.push(`+${prCount}P PR`);
    if (achCount > 0)  parts.push(`+${achCount}P Ach`);
    if (parts.length > 1) {
      rows.push(
        <div key="formula" className="tip-sep" />,
        <div key="total" className="tip-row tip-total">{parts.join(' ')} = <strong>{formatPoints(total)}</strong></div>
      );
    } else {
      rows.push(
        <div key="total" className="tip-sep" />,
        <div key="totalone" className="tip-row tip-total">Total: <strong>{formatPoints(total)}</strong></div>
      );
    }
  }

  return rows;
}

export default function ActivityChip({ activity, variant, ownerAvatar, isBot, showOppPoints = false, hideTooltip = false, challenge = null }: Props) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [showTip, setShowTip] = useState(false);
  const [tipStyle, setTipStyle] = useState<React.CSSProperties>({});
  const isTouch = useRef(typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches);
  useScrollDismiss(showTip, setShowTip);

  const computeStyle = useCallback((): React.CSSProperties => {
    if (!cardRef.current) return {};
    const r         = cardRef.current.getBoundingClientRect();
    const tipW      = 240;
    let left        = Math.round(r.left);
    if (left + tipW > window.innerWidth - 8) left = window.innerWidth - tipW - 8;
    if (left < 8) left = 8;
    // prefer above; fall back to below if not enough space
    const spaceAbove = r.top;
    if (spaceAbove >= 160) {
      return { position: 'fixed', bottom: Math.round(window.innerHeight - r.top + 6), left };
    }
    return { position: 'fixed', top: Math.round(r.bottom + 6), left };
  }, []);

  const open  = useCallback(() => { setTipStyle(computeStyle()); setShowTip(true);  }, [computeStyle]);
  const close = useCallback(() => setShowTip(false), []);

  // close on outside click (touch)
  useEffect(() => {
    if (!showTip || !isTouch.current) return;
    const handler = () => close();
    document.addEventListener('click', handler, { once: true });
    return () => document.removeEventListener('click', handler);
  }, [showTip, close]);

  const handleMouseEnter = () => { if (!isTouch.current) open();  };
  const handleMouseLeave = () => { if (!isTouch.current) close(); };
  const handleClick      = (e: React.MouseEvent) => {
    if (!isTouch.current) return;
    e.stopPropagation();
    showTip ? close() : open();
  };

  const sportIcon     = getSportIcon(activity.sport_type);
  const totalPointsRaw = activity.total_points != null ? Number(activity.total_points) : null;
  const totalPoints   = totalPointsRaw ?? 0;
  const basePoints    = Number(activity.base_points) || 0;
  const bonusPoints   = Number(activity.challenge_bonus_points) || 0;
  const prBonusPoints = Number(activity.pr_bonus_points) || 0;
  const prCount       = activity.pr_count ?? 0;
  const achCount      = Math.min(activity.achievement_count ?? 0, 25);
  // pr_bonus_points enthält bereits pr_count + min(achievement_count, 25)
  // → achCount NICHT nochmal addieren (sonst Doppel-Zählung)
  const bonusTotal    = bonusPoints + prBonusPoints;
  const isManual      = !!activity.is_manual_entry;
  const isConsolation = activity.sport_type === 'Consolation';
  const isBetting     = activity.sport_type === 'Betting';
  const isRestDay     = activity.sport_type === 'RestDay';
  const isChallengeKing = activity.sport_type === 'ChallengeKing';
  const isMultisport    = activity.sport_type === 'Multisport';
  const isSportJunky = activity.sport_type === 'SportJunky';
  const isDailySpin = activity.sport_type === 'DailySpin';
  const isSpecial     = isConsolation || isBetting || isRestDay || isChallengeKing || isMultisport || isSportJunky || isDailySpin || activity.sport_type === 'BadgeReward';
  const stravaUrl     = (activity.strava_activity_id && activity.strava_activity_id > 0 && !isConsolation)
    ? `https://www.strava.com/activities/${activity.strava_activity_id}`
    : null;

  const tooltip = showTip && !hideTooltip
    ? ReactDOM.createPortal(
        <div className="chip-tooltip" style={tipStyle}>
          {buildTooltip(activity, challenge)}
        </div>,
        document.body
      )
    : null;

  const card = (
    <>
      <div
        ref={cardRef}
        className={`activity-card activity-card--${variant}${isManual && !isSpecial ? ' activity-card--manual' : ''}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <div className="activity-card-image">
          {/* Obere Hälfte: Sport-Icon + Badges unten */}
          <div className="activity-card-image-top">
            {activity.sport_type === 'BadgeReward' && (activity as any).raw_json ? (
              (() => {
                let raw: any;
                try { raw = typeof (activity as any).raw_json === 'string' ? JSON.parse((activity as any).raw_json) : (activity as any).raw_json; } catch { raw = null; }
                const badges = raw?.badges;
                if (Array.isArray(badges) && badges.length > 0) {
                  const show = badges.slice(0, 6);
                  const iconSize = badges.length <= 3 ? 26 : badges.length <= 4 ? 22 : 18;
                  return (
                    <div style={{ display: 'flex', gap: 3, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', padding: '2px 0' }}>
                      {show.map((b: any, i: number) => (
                        <div key={i} style={{
                          width: iconSize, height: iconSize, borderRadius: '50%',
                          border: '2px solid #e6b800', background: 'rgba(230,184,0,0.12)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#e6b800', fontSize: iconSize * 0.5 + 'px',
                        }} title={`${b.name} (Tier ${b.tier}) +${b.weekly_reward} pts/week`}>
                          <FontAwesomeIcon icon={getSportIcon(b.icon || 'BadgeReward')} />
                        </div>
                      ))}
                      {badges.length > 6 && <span style={{ fontSize: '0.55rem', color: '#e6b800' }}>+{badges.length - 6}</span>}
                    </div>
                  );
                }
                return <FontAwesomeIcon icon={sportIcon} className="activity-card-emoji" style={activity.sport_type === 'BadgeReward' ? { color: '#ffb63c' } : isSpecial ? { color: '#81c784' } : undefined} />;
              })()
            ) : (
              <>
                <FontAwesomeIcon icon={sportIcon} className="activity-card-emoji" style={activity.sport_type === 'BadgeReward' ? { color: '#ffb63c' } : isSpecial ? { color: '#81c784' } : undefined} />
                {bonusPoints > 0 && !isSpecial && !activity.trophy_boosted && <FontAwesomeIcon icon={faMedal} className="activity-card-challenge-icon" />}
                {activity.handcuff_penalty ? <FontAwesomeIcon icon={faHandcuffs} className="activity-card-handcuff-icon" title="Handcuffed: base points halved" /> : null}
                {activity.strike_first_holder ? <FontAwesomeIcon icon={faBolt} className="activity-card-strike-icon" title="Strike First holder" /> : null}
                {activity.revealed && <FontAwesomeIcon icon={faSearch} className="activity-card-reveal-icon" title="Revealed by item" />}
                {activity.trophy_boosted && <FontAwesomeIcon icon={faTrophy} className="activity-card-trophy-icon" title={`Trophy: +${activity.trophy_boost_percent}% boost (+${activity.trophy_boost_amount}P)`} />}
                {variant === 'opponent' && ownerAvatar && !isBot && (
                  <img src={ownerAvatar} alt="" className="activity-card-avatar" />
                )}
                {variant === 'opponent' && isBot && (
                  <FontAwesomeIcon icon={faRobot} className="activity-card-avatar activity-card-avatar--bot" />
                )}
                {(prCount > 0 || achCount > 0) && (
                  <div className="activity-card-top-badges">
                    {prCount > 0 && <span className={`chip-pr${variant === 'opponent' ? ' chip-pr--opp' : ''}`}><FontAwesomeIcon icon={faTrophy} /></span>}
                    {achCount > 0 && <span className={`chip-ach${variant === 'opponent' ? ' chip-ach--opp' : ''}`}><FontAwesomeIcon icon={faBolt} /></span>}
                  </div>
                )}
              </>
            )}
          </div>
          {/* Untere Hälfte: Minuten (links) + Punkte (rechts) */}
          <div className="activity-card-image-bottom">
            {!isSpecial && activity.duration_seconds > 0 && (
              <span className="activity-card-duration-mini">{Math.round(activity.duration_seconds / 60)}m</span>
            )}
            <div className="activity-card-image-pts">
              {variant === 'mine' ? (
                <span className="activity-card-points">
                  <span className="activity-card-points--base">{totalPointsRaw != null ? formatPoints(totalPoints) : '?'}</span>
                </span>
              ) : (
                showOppPoints || isConsolation || activity.revealed || activity.trophy_boosted
                  ? <span className="activity-card-points activity-card-points--opp">{totalPointsRaw != null ? formatPoints(totalPoints) : '?'}</span>
                  : <span className="activity-card-points activity-card-points--hidden">?</span>
              )}
            </div>
          </div>
        </div>
      </div>
      {tooltip}
    </>
  );

  if (variant === 'mine' && stravaUrl && !isTouch.current) {
    return (
      <a href={stravaUrl} target="_blank" rel="noopener noreferrer" className="chip-strava-link">
        {card}
      </a>
    );
  }

  return card;
}
